import React, { useState, useEffect, useContext } from "react";
import { useCookies } from "react-cookie";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../utils/storageUtil";
import config from "../config/config";
import { makeUnique, useCallV2API } from "../utils/helperFunctions";
import apiList from "../services/apiList";
import { callApi } from "../services/callApi";
import {
  DATA_REFERENCE,
  MOVEMENT_KEY,
  SECTOR_KEY,
  TERMINAL_KEY,
} from "utils/Constants";
import {
  kiosk_movementType,
  kiosk_sector,
  kiosk_terminal,
} from "config/envConfig";
import { DeliveryOptionContext } from "./deliveryOptionContext";

const CartContext = React.createContext();

const CartProvider = (props) => {
  const useDelivery = useContext(DeliveryOptionContext);

  const [cookies, setCookie] = useCookies(["cart"]);
  const [cart, setCart] = useState(getLocalStorage("cart") || {});
  const callV2API = useCallV2API();

  useEffect(() => {
    setLocalStorage("cart", cart);
    console.log("change in cart", cart);
  }, [cart]);

  function set(cartObj) {
    let obj = JSON.parse(JSON.stringify(cartObj));
    setCart(obj);
    setCookie("cart", JSON.stringify(obj), {
      path: "/",
    });
    console.log("set cart", obj);
  }

  function reset() {
    setCart({});
    removeLocalStorage("cart");
    setCookie("cart", JSON.stringify(cart), {
      path: "/",
    });
  }

  function addItem(params) {
    const storeId = params.storecode;
    const storeNm = params.storename;

    const movementType = params?.movementType;
    const sector = params?.sector;
    const terminal = params?.terminal;
    const fulfilmentType = params?.fulfilmentType;
    const domain = params?.domain;

    let newItem = params.item;
    if (!newItem.deliveryOptions) {
      newItem.deliveryOptions = {};
    }

    let cartObj = cart;

    if (cartObj) {
      if (void 0 === cartObj[storeId]) {
        cartObj[storeId] = {
          storeId,
          storeNm,
          movementType,
          sector,
          terminal,
          fulfilmentType,
          domain,
          items: [],
          metadata: params.metadata || null,
        };
      }
      if (!this.hasItem(params)) {
        cartObj[storeId].items.push(newItem);
      }
      this.set(cartObj);

      return true;
    } else {
      this.reset();
      return false;
    }
  }

  function removeItem(params) {
    const storeId = params.storecode;
    let addOnFlag = false;
    let preferenceFlag = false;

    if (cart && cart[storeId]) {
      cart[storeId].items = cart[storeId].items.filter((x) => {
        if (x.itemId === params.productId) {
          if (x.addOn && params.addOn) {
            addOnFlag = compareAddOns(x.addOn, params.addOn) ? false : true;
          }
          if (x.customisationPreferences && params.customisationPreferences) {
            preferenceFlag = comparePreferences(
              x.customisationPreferences,
              params.customisationPreferences
            )
              ? false
              : true;
          }
          if (addOnFlag || preferenceFlag) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      });
      setCookie("cart", JSON.stringify(cart), {
        path: "/",
      });
    }
  }

  function updateItem(params) {
    let flag = false;
    let addOnFlag = true;
    let preferenceFlag = true;

    if (cart && cart[params.storecode]) {
      const _cart = JSON.parse(JSON.stringify(cart));
      _cart[params.storecode].items.map((x) => {
        if (
          x.itemId === params.productId ||
          params.pVariables?.includes(x.itemId)
        ) {
          flag = true;
          if (x.addOn && params.addOn) {
            addOnFlag = compareAddOns(x.addOn, params.addOn) ? true : false;
          }
          if (x?.customisationPreferences && params?.customisationPreferences) {
            preferenceFlag = comparePreferences(
              x?.customisationPreferences ?? [],
              params?.customisationPreferences ?? []
            )
              ? true
              : false;
          }
          if (addOnFlag && preferenceFlag) {
            flag = true;
          } else {
            flag = false;
          }
          if (flag) {
            x.itemQuantity = params.quantity;
          }
        }
      });
      setCart(_cart);
      setCookie("cart", JSON.stringify(_cart), {
        path: "/",
      });
    }
  }

  function updateCompleteItem(params) {
    const { isEdit } = params;
    const { addOn, customisationPreferences, pVariables, itemId, feGenId } =
      params.item;
    if (cart && cart[params.storecode]) {
      cart[params.storecode].items.forEach((x) => {
        const isProductMatch =
          x.itemId === itemId ||
          (pVariables && pVariables.includes(x.itemId)) ||
          x.feGenId === feGenId;

        let isAddOnMatch = true;
        let isPreferencesMatch = true;

        if (addOn && x.addOn) {
          isAddOnMatch = compareArrayOfObjects(x.addOn, addOn);
        }
        if (customisationPreferences && x.customisationPreferences) {
          isPreferencesMatch = compareArrayOfObjects(
            x.customisationPreferences,
            customisationPreferences
          );
        }
        if (isProductMatch) {
          if (isEdit) {
            Object.assign(x, params.item);
          } else {
            if (isAddOnMatch && isPreferencesMatch) {
              Object.assign(x, params.item);
            }
          }
        }
      });
      setCookie("cart", JSON.stringify(cart), {
        path: "/",
      });
    }
  }

  function updateSelectedAddon(params) {
    if (cart && cart[params.storecode]) {
      cart[params.storecode].items.forEach(
        (x) => x.itemId === params.productId && (x.addOn = params.addOn)
      );
      setCookie("cart", JSON.stringify(cart), {
        path: "/",
      });
    }
  }

  function updateSelectedAttributeList(params) {
    if (cart && cart[params.storecode]) {
      cart[params.storecode].items.forEach((x) => {
        if (x.itemId === params.productId) {
          x.preferences = params.preferences;
          x.customisationPreferences = params.customisationPreferences;
        }
      });
      setCookie("cart", JSON.stringify(cart), {
        path: "/",
      });
    }
  }

  function getCart() {
    return cart;
  }

  function getItem(params) {
    let cItem;
    const storeId = params.storecode;
    let flag = false;
    let addOnFlag = false;
    let preferenceFlag = false;
    if (cart && cart[storeId]) {
      cart[storeId].items.forEach((x) => {
        if (x.itemId === params.productId) {
          flag = true;
          if (x.addOn && params.addOn) {
            addOnFlag = compareAddOns(x.addOn, params.addOn) ? true : false;
          }
          if (x?.customisationPreferences && params?.customisationPreferences) {
            preferenceFlag = comparePreferences(
              x?.customisationPreferences ?? [],
              params?.customisationPreferences ?? []
            )
              ? true
              : false;
          }
          if (addOnFlag && preferenceFlag) {
            flag = true;
          } else {
            flag = false;
          }
          if (flag) {
            cItem = x;
          }
        }
      });
    }
    return cItem;
  }

  function getItems(_obj) {
    let items = [];
    const obj = _obj ? _obj : cart;

    obj && Object.values(obj).forEach((x) => (items = items.concat(x.items)));

    return items;
  }

  function getStoreItems(storeId) {
    return cart && cart[storeId] ? cart[storeId].items : [];
  }

  function isEmpty(_obj) {
    let flag = true;
    const obj = _obj ? _obj : cart;

    if (obj) {
      for (let store in obj) {
        if (obj[store]) {
          0 < obj[store].items.length && (flag = false);
        }
      }
    }

    return flag;
  }

  function hasItem(params) {
    const storeId = params.storecode;
    let flag = false;

    if (cart && cart[storeId]) {
      cart[storeId].items.forEach((x) => {
        if (
          x.itemId === params.productId ||
          params.pVariables?.includes(x.itemId)
        ) {
          flag = true;
          if (x.addOn && params.addOn) {
            flag = compareAddOns(x.addOn, params.addOn) ? true : false;
          }
          if (x.customisationPreferences && params.customisationPreferences) {
            flag = comparePreferences(
              x.customisationPreferences,
              params.customisationPreferences
            )
              ? true
              : false;
          }
        }
      });
    }
    return flag;
  }

  function updateDeliveryOptions(updatedDeliveryOptions) {
    getItems().forEach((x) => (x.deliveryOptions = updatedDeliveryOptions));
    let fulfilmentType = config.productFulfilmentType.takeaway;
    if (
      updatedDeliveryOptions.deliveryOption ===
      config.deliveryOptions.deliveryAtGate
    ) {
      fulfilmentType = config.productFulfilmentType.delivery;
    }
    let localCart = JSON.parse(JSON.stringify(getCart()));
    if (!isEmpty() && Object.values(localCart).length > 0) {
      Object.values(localCart).forEach(
        (x) => (x.fulfilmentType = fulfilmentType)
      );
    }
    this.set(localCart);
  }

  function updateMetaData(terminal, movementType, sector, fulfilmentType) {
    let localCart = JSON.parse(JSON.stringify(getCart()));
    if (!isEmpty() && Object.values(localCart).length > 0) {
      Object.values(localCart).forEach((x) => {
        x.movementType = movementType;
        x.sector = sector;
        x.terminal = terminal;
        x.fulfilmentType = fulfilmentType;
      });
    }
    this.set(localCart);
  }

  let compareTwoArrayOfObjects = (
    first_array_of_objects,
    second_array_of_objects
  ) => {
    return (
      first_array_of_objects &&
      second_array_of_objects &&
      first_array_of_objects.length !== 0 &&
      second_array_of_objects.length !== 0 &&
      first_array_of_objects.length === second_array_of_objects.length &&
      first_array_of_objects.every((element_1) =>
        second_array_of_objects.some((element_2) =>
          Object.keys(element_1).every(
            (key) => element_1[key] === element_2[key]
          )
        )
      )
    );
  };

  const compareAddOns = (result1, result2) => {
    result1.sort(function (a, b) {
      if (a.addonId < b.addonId) {
        return -1;
      }
      if (a.addonId > b.addonId) {
        return 1;
      }
      return 0;
    });
    result2.sort(function (a, b) {
      if (a.addonId < b.addonId) {
        return -1;
      }
      if (a.addonId > b.addonId) {
        return 1;
      }
      return 0;
    });

    if (result1.length !== result2.length) {
      return false;
    } else {
      // let result = result1.filter(
      //   (o1) => !result2.some((o2) => o1.addonId === o2.addonId)
      // );
      // if (result.length > 0) {
      //   return false;
      // } else {
      //   return true;
      // }
      if (JSON.stringify(result1) === JSON.stringify(result2)) {
        return true;
      } else {
        return false;
      }
    }
  };

  const comparePreferences = (arr1 = [], arr2 = []) => {
    if (arr1.length !== arr2.length) return false;

    const normalize = (arr) =>
      arr
        .map((pref) => {
          const obj = { ...pref };

          if (Array.isArray(obj.selection)) {
            obj.selection = obj.selection
              .map((sel) => ({ ...sel }))
              .sort((a, b) => (a._id > b._id ? 1 : -1));
          }

          return obj;
        })
        .sort((a, b) => (a.title > b.title ? 1 : -1));

    const r1 = normalize(arr1);
    const r2 = normalize(arr2);

    return r1.every((p1) =>
      r2.some((p2) => {
        // 👉 compare only common keys of preference object
        const commonPrefKeys = Object.keys(p1).filter((k) =>
          Object.prototype.hasOwnProperty.call(p2, k)
        );

        const prefMatch = commonPrefKeys.every((key) =>
          key !== "selection"
            ? JSON.stringify(p1[key]) === JSON.stringify(p2[key])
            : true
        );

        if (!prefMatch) return false;

        // 👉 compare selection only on common keys
        if (p1.selection && p2.selection) {
          if (p1.selection.length !== p2.selection.length) return false;

          return p1.selection.every((s1) =>
            p2.selection.some((s2) => {
              const commonSelKeys = Object.keys(s1).filter((k) =>
                Object.prototype.hasOwnProperty.call(s2, k)
              );

              return commonSelKeys.every(
                (k) => JSON.stringify(deepNormalize(s1[k])) ===
                JSON.stringify(deepNormalize(s2[k]))
              );
            })
          );
        }

        return true;
      })
    );
  };

  const deepNormalize = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(deepNormalize);
    }

    if (obj && typeof obj === "object") {
      const sorted = {};

      Object.keys(obj)
        .sort()
        .forEach((key) => {
          let value = deepNormalize(obj[key]);

          if (
            (key === "selection" || key === "subSelections") &&
            Array.isArray(value)
          ) {
            value = [...value].sort((a, b) => {
              const keyA =
                a?.preferenceSku ||
                a?.addonId ||
                a?.title ||
                a?.name ||
                a?.quantity ||
                "";

              const keyB =
                b?.preferenceSku ||
                b?.addonId ||
                b?.title ||
                b?.name ||
                b?.quantity ||
                "";

              return String(keyA).localeCompare(String(keyB));
            });
          }

          sorted[key] = value;
        });

      return sorted;
    }

    return obj;
  };

  const removeItemById = (params) => {
    const storeId = params.storecode;
    const productId = params.productId;
    const { addOn, customisationPreferences, pVariables } = params;

    if (!cart[storeId]) return;

    cart[storeId].items = cart[storeId].items.filter((x) => {
      // Check for productId and pVariables
      const isProductMatch =
        x.itemId === productId || (pVariables && pVariables.includes(x.itemId));

      // Check for addOn and customisationPreferences match if provided
      let isAddOnMatch = true;
      let isPreferencesMatch = true;

      if (addOn && x.addOn) {
        isAddOnMatch = compareArrayOfObjects(x.addOn, addOn);
      }
      if (customisationPreferences && x?.customisationPreferences) {
        isPreferencesMatch = compareArrayOfObjects(
          x?.customisationPreferences ?? [],
          customisationPreferences ?? []
        );
      }

      if (
        isProductMatch &&
        (!addOn || !x.addOn || isAddOnMatch) &&
        (!customisationPreferences ||
          !x.customisationPreferences ||
          isPreferencesMatch)
      ) {
        return false;
      }
      return true;
    });

    if (cart[storeId].items.length === 0) {
      delete cart[storeId];
    }
    set(cart);
  };

  // ------------------------------------------ NEW -------------------------------------------------------

  function isAlreadyAdded(params) {
    const storeId = params.storeId;

    if (cart && cart[storeId]) {
      const items = cart[storeId].items;

      const isAddOnPreferencesAvailable =
        params.addOn !== undefined ||
        params.customisationPreferences !== undefined;

      let variablesMatched = false;

      for (const item of items) {
        if (params?.item?.pVariables) {
          variablesMatched = params.item.pVariables.includes(item.itemId);
        }
        if (isAddOnPreferencesAvailable) {
          if (variablesMatched || item.itemId === params.productId) {
            let addOnMatched = false;
            let customisationPreferencesMatched = false;
            if (params.addOn && item.addOn) {
              addOnMatched = compareArrayOfObjects(item.addOn, params.addOn);
            }
            if (
              params.customisationPreferences &&
              item.customisationPreferences
            ) {
              customisationPreferencesMatched = compareArrayOfObjects(
                params.customisationPreferences,
                item.customisationPreferences
              );
            }
            if (addOnMatched && customisationPreferencesMatched) {
              return true;
            } else {
              return false;
            }
          }
        } else {
          if (variablesMatched || item.itemId === params.productId) {
            return true;
          }
        }
      }
      return false;
    }
  }

  // Used in Cart Only
  function isPresentInCart(params) {
    const storeId = params.storeId;

    if (cart && cart[storeId]) {
      const items = cart[storeId].items;

      for (const item of items) {
        if (
          item.itemId === params.productId &&
          compareArrayOfObjects(item.addOn, params.addOn) &&
          compareArrayOfObjects(
            item.customisationPreferences,
            params.customisationPreferences
          )
        ) {
          return true;
        }
      }
      return false;
    }
  }

  function updateProductItem(params) {
    let addOnUpdated = false;
    let preferenceUpdated = false;

    let updateAddOnPreferences =
      params.addOn !== undefined ||
      params.customisationPreferences !== undefined;

    if (cart && cart[params.storeId]) {
      const items = cart[params.storeId].items;
      let matchingItems;
      if (params?.pVariables?.length > 0 && params?.pVariables !== undefined) {
        matchingItems = items.filter((item) =>
          params?.pVariables.includes(item.itemId)
        );
      } else {
        matchingItems = items.filter(
          (item) => item.itemId === params.productId
        );
      }

      if (updateAddOnPreferences) {
        for (const item of items) {
          let variablesMatched = false;
          if (params?.item?.pVariables) {
            variablesMatched = params.item.pVariables.includes(item.itemId);
          }

          const addOnMatched = compareArrayOfObjects(
            item.addOn,
            params.item.addOn || []
          );
          const customisationPreferencesMatched = compareArrayOfObjects(
            item.customisationPreferences,
            params.item.customisationPreferences || []
          );

          if (
            (variablesMatched || item.itemId === params.productId) &&
            addOnMatched &&
            customisationPreferencesMatched
          ) {
            if (item.addOn && params.addOn && params.item.addOn) {
              addOnUpdated = compareArrayOfObjects(item.addOn, params.addOn)
                ? false
                : true;
            }
            if (
              item.customisationPreferences &&
              params.customisationPreferences &&
              params.item.customisationPreferences
            ) {
              preferenceUpdated = compareArrayOfObjects(
                item.customisationPreferences,
                params.customisationPreferences
              )
                ? false
                : true;
            }
            if (addOnUpdated) {
              item.addOn = params.addOn;
            }
            if (preferenceUpdated) {
              item.customisationPreferences = params.customisationPreferences;
            }
            if (!addOnUpdated || !preferenceUpdated) {
              if (params.quantity <= 0) {
                const index = items.indexOf(item);
                items.splice(index, 1);
                cart[params.storeId].items = items;
                if (items.length === 0) {
                  delete cart[params.storeId];
                }
              } else {
                item.itemQuantity = params.quantity;
              }
            }
          }
        }
      } else {
        for (const item of matchingItems) {
          let variablesMatched = false;
          if (
            params?.pVariables?.length > 0 &&
            params?.pVariables !== undefined
          ) {
            variablesMatched = params.pVariables.includes(item.itemId);
          }
          if (variablesMatched) {
            if (params.quantity <= 0) {
              items.pop();
              cart[params.storeId].items = items;
              if (items.length === 0) {
                delete cart[params.storeId];
              }
              this.set(cart);
              return;
            } else {
              matchingItems[matchingItems.length - 1].itemQuantity =
                params.quantity;
            }
          } else if (item.itemId === params.productId) {
            if (params.quantity <= 0) {
              const index = items.indexOf(item);
              items.splice(index, 1);
              cart[params.storeId].items = items;
              if (items.length === 0) {
                delete cart[params.storeId];
              }
            } else {
              item.itemQuantity = params.quantity;
            }
          }
        }
      }
      this.set(cart);
    }
  }

  function updateRepatedItem(params) {
    let updated = false;
    let addOnUpdated = false;
    let preferenceUpdated = false;
    if (cart && cart[params.storeId]) {
      cart[params.storeId].items.forEach((x) => {
        const addOnMatched = compareArrayOfObjects(x.addOn, params.item.addOn);
        const customisationPreferencesMatched = compareArrayOfObjects(
          x.customisationPreferences,
          params.item.customisationPreferences
        );

        if (
          x.itemId === params.productId &&
          addOnMatched &&
          customisationPreferencesMatched
        ) {
          updated = false;
          if (x.addOn && params.item.addOn) {
            if (x.addOn && params.addOn) {
              addOnUpdated = compareArrayOfObjects(x.addOn, params.addOn)
                ? false
                : true;
            }
          }
          if (
            x.customisationPreferences &&
            params.item.customisationPreferences &&
            customisationPreferencesMatched
          ) {
            if (x.customisationPreferences && params.customisationPreferences) {
              preferenceUpdated = compareArrayOfObjects(
                x.customisationPreferences,
                params.customisationPreferences
              )
                ? false
                : true;
            }
          }
          if (addOnUpdated) {
            x.addOn = params.addOn;
          }
          if (preferenceUpdated) {
            x.customisationPreferences = params.customisationPreferences;
            if (params.preferences !== undefined) {
              x.preferences = params.preferences;
            }
          }
          if (addOnUpdated || preferenceUpdated) {
            updated = true;
          } else {
            updated = false;
          }
          if (updated) {
            if (params.quantity <= 0) {
              removeProductItem(params);
            } else {
              x.itemQuantity = params.quantity;
            }
          }
        }
      });
      this.set(cart);
    }
  }

  function removeProductItem(params) {
    const storeId = params.storeId;
    let addOnFlag = false;
    let preferenceFlag = false;

    if (cart && cart[storeId]) {
      cart[storeId].items = cart[storeId].items.filter((x) => {
        if (x.itemId === params.productId) {
          if (x.addOn && params.addOn) {
            addOnFlag = compareArrayOfObjects(x.addOn, params.addOn)
              ? false
              : true;
          }
          if (
            x.customisationPreferences > 0 &&
            params.customisationPreferences > 0
          ) {
            preferenceFlag = compareArrayOfObjects(
              x.customisationPreferences,
              params.customisationPreferences
            )
              ? false
              : true;
          }
          if (addOnFlag || preferenceFlag) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      });
      this.set(cart);
    }
  }

  function getItemQunatity(params) {
    let qty;
    const storeId = params.storeId;
    let flag = false;
    let addOnFlag = true;
    let preferenceFlag = true;
    if (cart && cart[storeId]) {
      cart[storeId].items.forEach((x) => {
        let variablesMatched = false;
        if (params?.item?.pVariables) {
          variablesMatched = params.item.pVariables.includes(x.itemId);
        }
        if (variablesMatched || x.itemId === params.productId) {
          flag = true;
          if (x.addOn && params.addOn) {
            addOnFlag = compareArrayOfObjects(x.addOn, params.addOn)
              ? true
              : false;
          }
          if (x.customisationPreferences && params.customisationPreferences) {
            preferenceFlag = compareArrayOfObjects(
              x.customisationPreferences,
              params.customisationPreferences
            )
              ? true
              : false;
          }
          if (addOnFlag && preferenceFlag) {
            flag = true;
          } else {
            flag = false;
          }
          if (flag) {
            qty = x.itemQuantity;
          }
        }
      });
    }
    return qty;
  }

  // Helper to deeply sort object keys
  function deepSortObject(obj) {
    if (Array.isArray(obj)) {
      return obj.map(deepSortObject);
    } else if (obj && typeof obj === "object") {
      const sortedObj = {};
      Object.keys(obj)
        .sort()
        .forEach((key) => {
          sortedObj[key] = deepSortObject(obj[key]);
        });
      return sortedObj;
    }
    return obj;
  }

  const compareArrayOfObjects = (arr1, arr2) => {
    // Sort arrays by addonId or title if present
    arr1 = arr1.slice().sort((a, b) => {
      if (a.addonId !== undefined && b.addonId !== undefined) {
        return a.addonId - b.addonId;
      }
      if (a.title && b.title) {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });
    arr2 = arr2.slice().sort((a, b) => {
      if (a.addonId !== undefined && b.addonId !== undefined) {
        return a.addonId - b.addonId;
      }
      if (a.title && b.title) {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

    // Sort selections inside objects if present
    [arr1, arr2].forEach((arr) => {
      arr.forEach((item) => {
        if (item.selection && Array.isArray(item.selection)) {
          item.selection = item.selection
            .slice()
            .sort((a, b) =>
              String(
                a._id ||
                a.preferenceSku ||
                a.addonId ||
                ""
              ).localeCompare(
                String(
                  b._id ||
                  b.preferenceSku ||
                  b.addonId ||
                  ""
                )
              )
            );
        }
      });
    });

    // Deep sort all objects before comparison
    const sortedArr1 = deepSortObject(arr1);
    const sortedArr2 = deepSortObject(arr2);

    if (sortedArr1.length !== sortedArr2.length) {
      return false;
    }
    return JSON.stringify(deepNormalize(sortedArr1)) === JSON.stringify(deepNormalize(sortedArr2));
  };

  const myCart = async () => {
    try {
      let obj = {};
      const apiUrl = callV2API ? apiList?.cart?.myCartV2: apiList?.cart?.myCart;
      let apiURL = `${apiUrl}?type=kiosk`;
      let apiResponse = await callApi.get(apiURL);
      if (apiResponse?.status === 200) {
        if (apiResponse?.data?.cart) {
          const data = callV2API ? apiResponse?.data : JSON.parse(apiResponse?.data?.kiosk);
          if (data?.items?.length > 0) {
            data?.items?.forEach((x) => {
              obj[x?.storeId] = x;
            });
            set(obj);
            return getItems(obj);
          } else {
            return {};
          }
        }
      }
    } catch (e) {
      console.log(e);
      return true;
    }
  };

  const mapProductToCartPayload = ({ pDetails }) => {
    const storeInfo = pDetails?.storeInfo || {};

    const deliveryOptions = useDelivery.selectedDeliveryOption;
    const data = JSON.parse(localStorage.getItem(DATA_REFERENCE));

    const terminal =
      data?.[TERMINAL_KEY] ||
      storeInfo?.terminal?.value ||
      kiosk_terminal ||
      "";

    const sector =
      data?.[SECTOR_KEY] || storeInfo?.sector?.value || kiosk_sector || "";

    const movementType =
      data?.[MOVEMENT_KEY] ||
      storeInfo?.movementType?.value ||
      kiosk_movementType ||
      "";

    let fulfilmentType = config?.productFulfilmentType?.takeaway || "";

    if (
      deliveryOptions?.deliveryOption === config.deliveryOptions.deliveryAtGate
    ) {
      fulfilmentType = config.productFulfilmentType.delivery;
    }

    return {
      storeId: storeInfo?._id,
      movementType,
      sector,
      terminal,
      fulfilmentType,
      domain: storeInfo?.domain || "",

      items: [
        {
          storeName: storeInfo?.storeDisplayName,
          storeImage: pDetails?.shopImage || "",
          storeId: storeInfo?._id,

          productSKU: pDetails?.productSku || "",
          itemId: pDetails?._id,
          itemName: pDetails?.productName,
          itemPrice:
            pDetails?.price ||
            pDetails?.pricing?.pricingHistory?.[0]?.basePrice ||
            0,

          itemQuantity: 1,
          itemType: pDetails?.type || "product",

          addOn: [],
          preferences: [],
          customisationPreferences: [],
          customisationAttributes: [],

          deliveryOptions: {
            deliveryOption:
              deliveryOptions?.deliveryOption || "collect at store",
            deliveryAddress: "",
            isPackagingRequested: true,
            deliveryTime: new Date().toISOString(),
            timezone: "Europe/Copenhagen",
          },

          flightId: "",
          flightUid: "",

          dietCategory: pDetails?.dietCategory || "",
          productImage: pDetails?.productImageUrl || [],

          feGenId: makeUnique(5),
          pVariables: [],
        },
      ],

      metadata: null,
    };
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        set,
        reset,
        addItem,
        removeItem,
        updateItem,
        updateSelectedAddon,
        updateCompleteItem,
        getCart,
        getItem,
        getItems,
        getStoreItems,
        isEmpty,
        hasItem,
        updateDeliveryOptions,
        updateSelectedAttributeList,
        removeItemById,
        isAlreadyAdded,
        isPresentInCart,
        updateProductItem,
        updateRepatedItem,
        removeProductItem,
        getItemQunatity,
        compareArrayOfObjects,
        updateMetaData,
        myCart,
        mapProductToCartPayload,
      }}
    >
      {props.children}
    </CartContext.Provider>
  );
};

export { CartProvider, CartContext };
